<template>
  <button
    type="button"
    class="aha-button"
    @click="$emit('click', $event)"
    :class="[
      `aha-button-${size}`,
      `aha-button-${variant}`,
      {
        'mr-4': hasMarginRight,
        'ml-4': hasMarginLeft,
      }
    ]"
    :disabled="disabled"
  >
    <aha-icon
      :name="iconName"
      height="18"
      width="20"
      viewBox="0 0 24 24"
      class="mr-1"
      v-if="iconName.length && !submitting"
    />
    <slot />
    <aha-spinner
      class="ml-2"
      v-if="submitting && !iconName.length"
      :variant="variant !== 'base' ? 'white' : 'dark'"
    />
  </button>
</template>

<script>
import AhaIcon from './Icon.vue'
import AhaSpinner from './Spinner.vue'

export default {
  components: {
    AhaIcon,
    AhaSpinner,
  },
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    size: {
      type: String,
      default: 'md',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconName: {
      type: String,
      default: '',
    },
    hasMarginRight: {
      type: Boolean,
      deafult: false,
    },
    hasMarginLeft: {
      type: Boolean,
      deafult: false,
    },
  },
}
</script>

<style lang="scss">
.aha-button {
  /* prevent the text inside the button from flicking */
  -webkit-transform-style: preserve-3d;
  -webkit-backface-visibility: hidden;
  -webkit-transform: translateZ(0);

  /* focus:outline-none didn't work */
  &:focus {
    outline: none !important;
  }

  &:active:not(:disabled) {
    @apply translate-y-1 shadow-none;
  }

  @apply font-bold text-base-10 min-w-10;
  @apply transform transition-all duration-100;
  @apply rounded-full inline-flex items-center justify-center;

  ::after {
    content: "";
    @apply absolute -top-0.25 w-full right-0 h-0.25;
  }

  &[disabled] {
    @apply opacity-50 cursor-not-allowed;
  }

  &:hover:not(:active):not(:disabled) {
    @apply translate-y-0.25;

    ::after {
      @apply h-0.5 -top-0.5;
    }
  }

  &:focus-visible {
    outline: 2px solid !important;
    outline-offset: 0px !important;
    outline-color: #000000 !important;
  }

}

.aha-button-primary {
  &:active:not(:disabled) {
    @apply bg-primary-100;
  }
  @apply bg-primary-90;
  @apply shadow-blue-md;

  &:hover:not(:active):not(:disabled) {
    @apply shadow-blue;
  }
}

.aha-button-light {
  &:active:not(:disabled) {
    @apply bg-primary-60;
  }

  @apply bg-primary-60 shadow-primary-75-md;

  &:hover:not(:active):not(:disabled) {
    @apply shadow-primary-75;
  }
}

.aha-button-base {
  &:active:not(:disabled) {
    @apply bg-secondary-40;
  }
  @apply bg-secondary-10 text-secondary-100;
  @apply shadow-base-md;
  &:hover:not(:active):not(:disabled) {
    @apply shadow-base;
  }
}

.aha-button-danger {
  &:active:not(:disabled) {
    @apply bg-red-100;
  }
  @apply bg-red-50;
  @apply shadow-red-md;
  &:hover:not(:active):not(:disabled) {
    @apply shadow-red;
  }
}

.aha-button-warning {
  &:active:not(:disabled) {
    @apply bg-yellow-100;
  }
  @apply bg-yellow-50;
  @apply shadow-yellow-md;
  &:hover:not(:active):not(:disabled) {
    @apply shadow-yellow;
  }
}

.aha-button-white {
  &:active:not(:disabled) {
    @apply bg-secondary-20;
  }
  @apply bg-base-0 text-secondary-100;
  @apply shadow-grey-md;
  &:hover:not(:active):not(:disabled) {
    @apply shadow-grey;
  }
}

.aha-button-success {
  &:active:not(:disabled) {
    @apply bg-green-80;
  }
  @apply bg-green-50;
  @apply shadow-green-md;
  &:hover:not(:active):not(:disabled) {
    @apply shadow-green;
  }
}

.aha-button-link {
  @apply bg-transparent text-base-100 shadow-none transform-none font-normal;
  &:hover:not(:active):not(:disabled) {
    @apply text-primary-90;
  }
}

.aha-button-xs {
  @apply px-0.5 py-0.5 text-1.5xs space-x-0.5;
}

.aha-button-sm {
  @apply px-2 py-1 text-xs space-x-1;
}

/* Design: 36px, below: 37px */
.aha-button-md {
  @apply px-3 py-1.5 text-sm space-x-2;
  @apply min-w-16;
}

/* Design: 40px, below: 40px */
.aha-button-lg {
  @apply px-3 py-2 text-sm space-x-2;
}

/* Design: 44px, below: */
.aha-button-xl {
  @apply px-4 py-2.5 text-sm h-10 space-x-2;
}

.loading-spinner {
  display: inline-table;
  line-height: 1;
  vertical-align: text-top;
  width: 1.25em;
  height: 1.25em;
  border: 0.15em  solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
  -webkit-animation: spin 0.75s linear infinite;
  &.black {
    border: 0.15em  solid rgba(49, 49, 49, 0.3);
    border-top-color: #313131;
  }
}
</style>
